import { useState } from 'react'
import { Button } from '../../design/components'
import type { InitiativeEntry, LiveParticipant } from './types'
import styles from './live.module.css'

export interface InitiativeStripProps {
  entries: readonly InitiativeEntry[]
  /** Who is in the encounter, for the "are they out of it" mark. */
  participants: readonly LiveParticipant[]
  currentIndex: number
  canControl: boolean
  /** Committed once, on drop — never mid-drag. */
  onReorder: (from: number, to: number) => void
  onSelect: (participantId: string) => void
  /** The selected participant, so a row can show itself picked. */
  selectedParticipantId: string | null
  /** Whose turn to pass. Sits in this row rather than in the tools — it belongs to the order. */
  onAdvance: () => void
  round: number
  busy: boolean
}

/**
 * The turn order as a row of portraits, above the map.
 *
 * This is what a table looks at: *whose turn is it, and who is next*. As portraits rather than
 * names because a face is recognised in a glance and a name has to be read — and because it sits
 * above the map where horizontal space is the thing there is plenty of.
 *
 * **The rows name participants**, so this strip is exactly as correct in a social encounter with
 * no map as in a fight with one. It used to key on tokens, and a token is a thing on a map, which
 * made half the encounters this app runs unable to have an order at all
 * (`docs/web-api/phase-10-kickoff.md` §1, decision 5). A participant is one *instance*, so a horde
 * of six takes six turns under six names rather than one between them.
 *
 * **Drag to reorder**, hand-rolled on pointer events like the map's tokens, with the same rule:
 * nothing is sent mid-gesture, one request on drop. The compact rail beside it keeps the
 * `OrderButtons` as the keyboard-operable path, so this gesture is an addition rather than the
 * only way in (`docs/web-api/phase-9-kickoff.md` §3.5).
 */
export function InitiativeStrip({
  entries,
  participants,
  currentIndex,
  canControl,
  onReorder,
  onSelect,
  selectedParticipantId,
  onAdvance,
  round,
  busy,
}: InitiativeStripProps) {
  /** Which position is being dragged, and where it currently hovers. */
  const [drag, setDrag] = useState<{ from: number; over: number } | null>(null)

  if (entries.length === 0) {
    return (
      <p className={styles.stripEmpty}>
        {canControl
          ? 'Nobody in the order. Introduce someone from the Cast tab.'
          : 'The Game Master has not set an order yet.'}
      </p>
    )
  }

  const order = drag === null ? entries : reordered(entries, drag.from, drag.over)

  const out = new Set(
    participants.filter((participant) => participant.isDead).map((p) => p.participantId),
  )

  return (
    <div className={styles.stripRow}>
      <ol className={styles.strip} aria-label="Turn order">
        {order.map((entry, index) => {
          const isCurrent = drag === null && index === currentIndex

          return (
            <li key={entry.entryId} className={styles.stripItem}>
              <button
                type="button"
                className={[
                  styles.stripFace,
                  isCurrent ? styles.stripCurrent : '',
                  entry.hasActed && !isCurrent ? styles.stripActed : '',
                  out.has(entry.participantId) ? styles.stripDead : '',
                  entry.participantId === selectedParticipantId ? styles.stripSelected : '',
                ].join(' ')}
                aria-current={isCurrent ? 'step' : undefined}
                aria-label={`${entry.label}${isCurrent ? ', current turn' : ''}${entry.hasActed ? ', acted' : ''}`}
                onClick={() => onSelect(entry.participantId)}
                {...(canControl
                  ? {
                      onPointerDown: (event: React.PointerEvent<HTMLButtonElement>) => {
                        event.currentTarget.setPointerCapture(event.pointerId)
                        setDrag({ from: index, over: index })
                      },
                      onPointerMove: (event: React.PointerEvent<HTMLButtonElement>) => {
                        if (drag === null) return
                        if (!event.currentTarget.hasPointerCapture(event.pointerId)) return

                        // Which slot the pointer is over, measured against the row rather than the
                        // dragged element — the element moves under the finger as the preview
                        // reorders, and measuring it would chase itself.
                        const row = event.currentTarget.closest('ol')
                        if (row === null) return

                        const faces = [...row.querySelectorAll('li')]
                        const over = faces.findIndex((face) => {
                          const box = face.getBoundingClientRect()
                          return event.clientX >= box.left && event.clientX <= box.right
                        })

                        if (over !== -1 && over !== drag.over) setDrag({ ...drag, over })
                      },
                      onPointerUp: (event: React.PointerEvent<HTMLButtonElement>) => {
                        if (drag === null) return
                        event.currentTarget.releasePointerCapture(event.pointerId)

                        const { from, over } = drag
                        setDrag(null)
                        if (from !== over) onReorder(from, over)
                      },
                    }
                  : {})}
              >
                <span className={styles.stripPosition} aria-hidden="true">
                  {index + 1}
                </span>

                <span className={styles.numbered}>
                  {entry.portraitUrl != null ? (
                    <img
                      className={styles.stripPortrait}
                      src={entry.portraitUrl}
                      alt=""
                      draggable={false}
                    />
                  ) : (
                    <span className={styles.stripInitial} aria-hidden="true">
                      {entry.label.slice(0, 1).toUpperCase()}
                    </span>
                  )}

                  {entry.ordinal !== null && (
                    <span className={styles.ordinalBadge} aria-hidden="true">
                      {entry.ordinal}
                    </span>
                  )}
                </span>

                <span className={styles.stripName}>{entry.label}</span>

                {entry.value !== null && (
                  <span className={styles.stripValue} aria-hidden="true">
                    {entry.value}
                  </span>
                )}
              </button>
            </li>
          )
        })}
      </ol>

      {/* The turn control lives with the order rather than in the tool column: passing the turn is
          the single most frequent thing a Game Master does, and it belongs beside the thing it
          advances rather than in a tab that might not be open. */}
      {canControl && (
        <div className={styles.stripTurn}>
          <span className={styles.stripRound}>Round {round}</span>
          <Button disabled={busy} onClick={onAdvance}>
            {currentIndex < 0 ? 'Start' : 'Next'}
          </Button>
        </div>
      )}
    </div>
  )
}

/** The list as it would look if the drag were committed here. Preview only. */
function reordered(
  entries: readonly InitiativeEntry[],
  from: number,
  to: number,
): InitiativeEntry[] {
  const next = [...entries]
  const [moved] = next.splice(from, 1)
  if (moved !== undefined) next.splice(to, 0, moved)
  return next
}
